import type { AdminAuditEntry } from '@mantelpiece/shared';
import { api } from '../lib/api';
import { formatDateTime, shortId } from '../lib/format';
import { go, href } from '../lib/route';
import { useLoad } from '../lib/useLoad';
import { Loading, Pager } from '../components/bits';
import { PageHead } from '../components/Shell';

const ACTION_LABEL: Record<AdminAuditEntry['action'], string> = {
  'account.password_reset': 'Reset password',
  'account.unlock': 'Unlocked',
  'account.signout_all': 'Signed out everywhere',
  'account.edit': 'Edited details',
  'photo.takedown': 'Took down photo',
};

const targetHref = (entry: AdminAuditEntry): string | null =>
  entry.targetKind === 'account'
    ? href(`/accounts/${entry.targetId}`)
    : entry.targetKind === 'device'
      ? href(`/frames/${entry.targetId}`)
      : null;

const describe = (entry: AdminAuditEntry): string => {
  const d = entry.detail;
  if (!d) return '';
  if (entry.action === 'account.edit') {
    const from = d['from'] as { name?: string; email?: string } | undefined;
    const to = d['to'] as { name?: string; email?: string } | undefined;
    const parts: string[] = [];
    if (from?.name !== to?.name) parts.push(`name “${from?.name}” → “${to?.name}”`);
    if (from?.email !== to?.email) parts.push(`email ${from?.email} → ${to?.email}`);
    return parts.join('; ');
  }
  if (typeof d['sessionsRevoked'] === 'number') {
    return `${d['sessionsRevoked']} session${d['sessionsRevoked'] === 1 ? '' : 's'} signed out`;
  }
  if (entry.action === 'photo.takedown') {
    return `sent by ${String(d['uploader'] ?? '?')}, was ${String(d['status'] ?? '?')}`;
  }
  return '';
};

/** The audit table, shared by the audit page and an account's own page. */
export const AuditList = ({ entries }: { readonly entries: readonly AdminAuditEntry[] }) =>
  entries.length === 0 ? (
    <p className="empty">Nothing yet.</p>
  ) : (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th scope="col">When</th>
            <th scope="col">Who</th>
            <th scope="col">Did</th>
            <th scope="col">To</th>
            <th scope="col">Detail</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => {
            const link = targetHref(entry);
            const label = entry.targetLabel ?? `${entry.targetKind} ${shortId(entry.targetId)}`;
            return (
              <tr key={entry.id}>
                <td>{formatDateTime(entry.createdAt)}</td>
                <td>
                  <a className="ref" href={href(`/accounts/${entry.actor.id}`)}>
                    <span className="ref-name">{entry.actor.name}</span>
                    <span className="ref-meta">{entry.actor.email}</span>
                  </a>
                </td>
                <td>{ACTION_LABEL[entry.action] ?? entry.action}</td>
                <td>{link ? <a href={link}>{label}</a> : label}</td>
                <td className="note">{describe(entry)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

interface Props {
  readonly token: string;
  readonly query: URLSearchParams;
}

export const Audit = ({ token, query }: Props) => {
  const offset = Number(query.get('offset')) || 0;
  const loaded = useLoad(() => api.listAudit(token, { offset }), [token, offset]);

  return (
    <>
      <PageHead
        eyebrow="Audit"
        title="What administrators have done"
        lede="Every write made through this console, newest first. Rows are never deleted."
      />
      <Loading loaded={loaded}>
        {(page) => (
          <>
            <AuditList entries={page.items} />
            <Pager page={page} noun="entries" onOffset={(next) => go('/audit', { offset: next })} />
          </>
        )}
      </Loading>
    </>
  );
};
